/* 
For exporting/ importing the database, to backup and restore data, so we won't lose when testing/ if things go wrong

DexieJS has its own export/ import ability, but not human readable: https://dexie.org/docs/ExportImport/dexie-export-import
 */
import { db, EntryAttributeDB, ItemTypeDB, UserDB } from "@/ts/DexieDB";

import FileSaver from "file-saver"
import Data from "./Data";
import CategoryData, { CatDB } from "./DataTypes/CategoryData";
import { EntryDB } from "./DataTypes/Entry";
import Helpers from "./Helpers";

// import {parse as JSON5parse} from 'json5' //json5/ json for humans- allows imperfections like trailing commas, single quotes, comments, etc
//@ts-ignore
import JSON5 from 'json5/dist'



export class ExportImport_
{
    VERSION_NUMBER:number=0;
    VERSION_DESCRIPTION:string = "Initial JSON version with importing user->(date->entries, cats)"

    //old function- to export by user->cat->entry, etc
    async ExportSortedByCat()
    {
        //so lets build the Object
        console.log("getting users");
        let json_for_export={} as any;
        let users_db = (await db.users.toArray());

        json_for_export.Users = new Array();
        for (let i = 0; i < users_db.length; i++) 
        {
            const user_db = users_db[i];
            let user = {/* id:user_db.id, */ name:user_db.name, date_updated:Helpers.DateToHumanReadable(user_db.date_updated), timestamp:user_db.timestamp, cats:new Array()};
            json_for_export.Users.push(user); 
      

            //maybe should just grab the cats for this user
            let cats_db = (await db.cats.where("id_user").equals(user_db.id).toArray());

            // cats_db.forEach(async cat_db => //for each runs synchronysly only- issues with trying to do things in order
            //es2018 has "for of" instead: https://stackoverflow.com/questions/37576685/using-async-await-with-a-foreach-loop
            for(let cat_db of cats_db)
            {
                let cat = {/* id:cat_db.id, */ 
                name:cat_db.name, 
                timestamp:cat_db.timestamp,
                date_updated:Helpers.DateToHumanReadable(cat_db.date_updated), 
                    color_bg: cat_db.color_bg,
                    color_txt: cat_db.color_txt,
                    icon: cat_db.icon,
                    entries: new Array(),
                };
                user.cats.push(cat);


                //could also do entries for this cat now
                let entries_db = (await db.entries.where("id_cat").equals(cat_db.id).toArray());
                for(let entry_db of entries_db)
                {
                    let entry_name = (await db.item_types.where("id").equals(entry_db.id_item_type).toArray())[0].name;
                    let entry = {/* id:entry_db.id, */ name:entry_name, 
                        timestamp:entry_db.timestamp,
                        date_updated:Helpers.DateToHumanReadable(entry_db.date_updated),
                        date_finished:entry_db.date_finished?Helpers.DateToHumanReadable(entry_db.date_finished):"",//could be undefined, so can't convert date if it is
                        has_duration:entry_db.has_duration,
                        attributes:new Array(),
                    };
                    cat.entries.push(entry)


                    //now also add any attributes
                    let attributes_db = (await db.entry_attributes.where("id_entry").equals(entry_db.id).toArray());
                    for(let attribute_db of attributes_db)
                    {
                        let attribute = {
                            /* id:attribute_db.id, */ 
                            name:attribute_db.name,
                            timestamp:attribute_db.timestamp,
                            
                            value:attribute_db.value,
                            rating:attribute_db.rating,
                            date_updated:Helpers.DateToHumanReadable(entry_db.date_updated),
                        };

                        entry.attributes.push(attribute);
                    }
                }
                
            };
        }
        //then should save to file
        this.ExportJSONToFile(json_for_export);
    }

    //new function- to export by user, date-> entry, which contains cat, and bottom key for what each cat is (timestamp, extra info beyond name)
    async Export()
    {
        
        //should convert database to straight json

        //so our database structure as of today is: (tables)
        //Users, cats, item types, entries, entry attributes

        //so it would make the most sense to organize by:
        /* 
        [
            Users:[
                {
                    name, id, date_updated,
                    cats:[
                        {
                            id!: number;
                            id_user!:number;//can hide/ we'll grab the user id when re-importing
                            name!: string;
                            color_bg!: string;
                            color_txt!: string;
                            icon!: string;
                            date_updated!: Date;

                            entries:
                            [
                                {                                                                
                                    id!: number;
                                    "name"- will be the item type name- 

                                    id_user!:number; //can hide/ we'll grab the user id when re-importing
                                    id_cat!:number;//can hide/ we'll grab the cat id when re-importing
                                    //id_item_type!:number;//can hide/ we'll see if it already exists when importing, or create a new one based off of "name"
                                    // item_type?:string="";
                                    
                                    date_updated!: Date;
                                    date_finished: Date|undefined;
                                    //attributes?:undefined|Array<EntryAttributeDB>; // wil be added below
                                    has_duration:boolean=false; //for if the entry has a duration/ will keep track of time passed
                                    attributes:[
                                    {
                                        id?: number;
                                        //id_user!:number;//can hide/ we'll grab the user id when re-importing
                                        //id_cat!:number;//can hide/ we'll grab the cat id when re-importing
                                        //id_item_type!:number; //can hide/ we'll grab when re-importing
                                        //id_entry!:number; //can hide/ we'll grab when re-importing

                                        name!: string;
                                        value?: string;
                                        date_updated!: Date;
                                        rating!:number;
                                    }
                                    ]
                                }
                            ]
                        }

                    ]
                },
            ]
        ]
         */

        //so lets build the Object
        console.log("getting users");
        let json_for_export={VERSION:this.VERSION_NUMBER, VERSION_DESCRIPTION:this.VERSION_DESCRIPTION} as any;
        let users_db = (await db.users.toArray());

        //all entries- for figuring out dates
        let entries_db_all = new Array();
        let dates = new Array();

        json_for_export.Users = new Array();
        for (let i = 0; i < users_db.length; i++) 
        {
            const user_db = users_db[i];
            let user = {/* id:user_db.id, */ name:user_db.name, date_updated:Helpers.DateToHumanReadable(user_db.date_updated), timestamp:user_db.timestamp, 
                    dates:new Array(),
                    cats:new Array(),
                    };
            json_for_export.Users.push(user); 
      

            //maybe should just grab the cats for this user
            let cats_db = (await db.cats.where("id_user").equals(user_db.id).toArray());

            // cats_db.forEach(async cat_db => //for each runs synchronysly only- issues with trying to do things in order
            //es2018 has "for of" instead: https://stackoverflow.com/questions/37576685/using-async-await-with-a-foreach-loop
            for(let cat_db of cats_db)
            {
                /* let cat = {
                name:cat_db.name, 
                timestamp:cat_db.timestamp,
                date_updated:Helpers.DateToHumanReadable(cat_db.date_updated), 
                    color_bg: cat_db.color_bg,
                    color_txt: cat_db.color_txt,
                    icon: cat_db.icon,
                    entries: new Array(),
                };
                user.cats.push(cat); */


                //could also do entries for this cat now
                let entries_db = (await db.entries.where("id_cat").equals(cat_db.id).toArray());
                for(let entry_db of entries_db)
                {
                    let entry_name = (await db.item_types.where("id").equals(entry_db.id_item_type).toArray())[0].name;
                    let entry = {/* id:entry_db.id, */ name:entry_name, 
                        timestamp:entry_db.timestamp,
                        date_updated:Helpers.DateToHumanReadable(entry_db.date_updated),
                        date_finished:entry_db.date_finished?Helpers.DateToHumanReadable(entry_db.date_finished):"",//could be undefined, so can't convert date if it is
                        has_duration:entry_db.has_duration,
                        attributes:new Array(),
                    };

                    // cat.entries.push(entry);

                    (entry_db as any).cat_name=cat_db.name; //for being able to get the cat name easily for putting out to file
                    entries_db_all.push(entry_db)

                    //for organizing by date- just get the first section/ date
                    let date = Helpers.DateToHumanReadable(entry_db.date_updated).split(" ")[0];
                    let date_exists=false;
                    for (const new_date of dates) 
                    {
                        if(new_date.date==date)
                        {
                            date_exists=true;
                            break;
                        }
                    }
                    if(!date_exists)
                        dates.push({date:date, entries:new Array()});


                    //now also add any attributes
                    let attributes_db = (await db.entry_attributes.where("id_entry").equals(entry_db.id).toArray());
                    for(let attribute_db of attributes_db)
                    {
                        let attribute = {
                            /* id:attribute_db.id, */ 
                            name:attribute_db.name,
                            timestamp:attribute_db.timestamp,
                            
                            value:attribute_db.value,
                            rating:attribute_db.rating,
                            date_updated:Helpers.DateToHumanReadable(entry_db.date_updated),
                        };

                        entry.attributes.push(attribute);
                    }
                }//end of entries
                
            };//end of cats

            
            //so lets organize by dates
            dates.sort(function(a,b){
                // Turn your strings into dates, and then subtract them
                // to get a value that is either negative, positive, or zero.
                
                let date_arr_a=a.date.split("/");
                let date_a = new Date();
                date_a.setFullYear(date_arr_a[0])
                date_a.setMonth(date_arr_a[1]-1);//-1 since it needs to be 0 based
                date_a.setDate(date_arr_a[2])

                let date_arr_b=b.date.split("/");
                let date_b = new Date();
                date_b.setFullYear(date_arr_b[0])
                date_b.setMonth(date_arr_b[1]-1);//-1 since it needs to be 0 based
                date_b.setDate(date_arr_b[2])
                
                return date_b.getTime() - date_a.getTime();
              });
              console.log("dates: ", dates);

              //make a human readable version for exporting
            // let dates_human_readable = dates.map((date)=>{
            //     return {date:Helpers.DateToHumanReadable(date), entries:new Array()};
            // });
            

            //now have to go through entries, and add them to the dates where applicable
            // for (const date of dates) 
            for(let i=0;i<dates.length;i++)
            {
                let date = dates[i];
                for (let entry_db of entries_db_all) 
                {
                    // console.log("entry_db.date_updated, ",entry_db.date_updated);
                    let date_updated_array = Helpers.DateToHumanReadable(entry_db.date_updated).split(" ");
                    let date_updated=date_updated_array[0];
                    // console.log("date_updated, ",date_updated, "date: ",date);
                    if(date_updated == date.date)
                    {
                        //convert it into a human readable entry and add it
                        let entry_name = (await db.item_types.where("id").equals(entry_db.id_item_type).toArray())[0].name;

                        //for date finished- if date is the same as started, just save out time
                        let date_finished = ""; //could be undefined, so can't convert date if it is
                        if(entry_db.date_finished)
                        {
                            date_finished=Helpers.DateToHumanReadable(entry_db.date_finished);
                            let date_finished_array = date_finished.split(" ");
                            if(date_finished_array[0]==date_updated)
                                date_finished = date_finished_array[1]+" "+date_finished_array[2]
                        }

                        let entry = {/* id:entry_db.id, */ name:entry_name, cat:entry_db.cat_name,
                            timestamp:entry_db.timestamp,
                            date_updated:date_updated_array[1]+" "+date_updated_array[2],
                            date_finished:date_finished,
                            has_duration:entry_db.has_duration,
                            attributes:new Array(),
                        };

                        //now also add any attributes
                        let attributes_db = (await db.entry_attributes.where("id_entry").equals(entry_db.id).toArray());
                        for(let attribute_db of attributes_db)
                        {
                            let attribute = {
                                /* id:attribute_db.id, */ 
                                name:attribute_db.name,
                                timestamp:attribute_db.timestamp,

                                value:attribute_db.value,
                                rating:attribute_db.rating,
                                // date_updated:Helpers.DateToHumanReadable(entry_db.date_updated),//dont need if storing under the date
                            };

                            entry.attributes.push(attribute);
                        }
                        if(entry.attributes.length==0)
                            (entry.attributes as any)=undefined;//get rid of it if empty

                        dates[i].entries.push(entry);
                    }
                }
                
            }//end of dates

            console.log("dates readable: ", dates);
            user.dates = dates;

            for(let cat_db of cats_db)
            {
                let cat = {/* id:cat_db.id, */ 
                    name:cat_db.name, 
                    timestamp:cat_db.timestamp,
                    type:cat_db.type,
                    date_updated:Helpers.DateToHumanReadable(cat_db.date_updated), 
                    color_bg: cat_db.color_bg,
                    color_txt: cat_db.color_txt,
                    icon: cat_db.icon,
                    // entries: new Array(),
                };
                user.cats.push(cat);
            }

        }//end of users
        console.log("json_for_export: ", json_for_export);



        //now do all the cats:
        /* let cats_db = (await db.cats.toArray());
        for (let i = 0; i < cats_db.length; i++) 
        {
            const cat_db = cats_db[i];

            let cat = {id:cat_db.id, name:cat_db.name, date_updated:cat_db.date_updated, 
                color_bg: cat_db.color_bg,
                color_txt: cat_db.color_txt,
                icon: cat_db.icon,
                entries: new Array(),
            };
            //then figure out which user this goes to, and add to them
            for (let j = 0; j < json_for_export.Users.length; j++) 
            {
                const user = json_for_export.Users[j];
                if(user.id == cat_db.id_user)
                {
                    user.cats.push(cat);                
                    break;
                }
            }
        }


        //now do all the entries:
        let entries_db = (await db.entries.toArray());
        // let item_types_db = (await db.item_types.toArray());
        
        //need to do entry attributes for each entry
        for (let i = 0; i < entries_db.length; i++) 
        {
            const entry_db = entries_db[i];

            
        } */

        

        //then should save to file
        this.ExportJSONToFile(json_for_export);
    }
    //someone made a hack function for saving json to text file: https://stackoverflow.com/questions/34156282/how-do-i-save-json-to-local-text-file
    ExportJSONToFile(json_obj:Object) 
    {
        /* const json_obj = {
          members: [{
              name: "cliff",
              age: "34"
            },
            {
              name: "ted",
              age: "42"
            },
            {
              name: "bob",
              age: "12"
            }
          ]
        }; */
      
        const a = document.createElement("a");
        let blob = new Blob([JSON.stringify(json_obj, null, 2)], {
            type: "text/plain"
          })
        a.href = URL.createObjectURL(blob);
        let date = new Date();
        let date_str = date.getFullYear()+"_"+(date.getMonth()+1)+"_"+date.getDate()+"__"+date.getHours()+"_"+date.getMinutes()+"_"+date.getSeconds();
        let file_name = "HealthDB_"+date_str+".txt";
        a.setAttribute("download", file_name);

        //other stuff to try to prompt a download instead of auto downloading to wherever
        // a.setAttribute("target ", "_blank");

        document.body.appendChild(a);
        
        


        a.click(); //works, but downloads without asking
        document.body.removeChild(a);

        //trying to get save as working:
        //going to try using SaveAs library: https://github.com/eligrey/FileSaver.js/
        //so looks like there is no current method to prompt with a "Save as" prompt O.o 
        //there is one in the works, but not yet: https://developer.mozilla.org/en-US/docs/Web/API/Window/showSaveFilePicker
        //as of 2021, that's the only answer....https://stackoverflow.com/questions/11336663/how-to-make-a-browser-display-a-save-as-dialog-so-the-user-can-save-the-conten
        //so stuck just saving/ no prompt for now

        // FileSaver.saveAs(blob, file_name);

      }
    async SetupImportButton()
    {
        const ele = document.createElement("input");
        ele.setAttribute("type", "file");
        ele.setAttribute("id", "file_input");
        ele.setAttribute("name", "file_input");
        ele.style.display="none";
        document.body.appendChild(ele);
        // element.appendChild(ele);


        /* const ele2 = document.createElement("label");
        ele2.setAttribute("for", "file_input");
        ele2.innerHTML="Click here to upload";
        document.body.appendChild(ele2); */

        ele.onchange = (event)=>{
        
            let file = (event as any).target.files[0];
            console.log("File onchange, event: "+event, "event file: ", file);

            const reader = new FileReader();
            reader.onload = async (event2)=>
            {
                let json = reader.result;
                // console.log("File read file, ",json);
                await this.ImportJSON(json as string);

                this.callback_finished_importing();
            };
            reader.readAsText(file);
        }
    }
    callback_finished_importing:any;
    async Import(callback_finished_importing:any)
    {
        console.log("import clicked");
        this.callback_finished_importing=callback_finished_importing;
        //from: https://stackoverflow.com/questions/31693296/is-it-possible-to-make-a-button-as-file-upload-button
        document.getElementById("file_input")?.click();

        //should prompt to open up file, to import and use

        //might have to create a fake file input field- only way to get users to be able to select a file is with input type file- so going to do hack like above
        //for loading local file: https://stackoverflow.com/questions/7346563/loading-local-json-file
        
        
        
        
        // ele.focus();
        // ele.click(); 
        // document.body.removeChild(ele);

        //for not having the file input ui showing: https://stackoverflow.com/questions/210643/in-javascript-can-i-make-a-click-event-fire-programmatically-for-a-file-input
        

        // console.log("clicked file input!");


        //should also both add to the database, and update if something is missing, but thing already exists (and needs to check id before trying to enter soemthing)
    }

    //to make sure things get unqiue timestamps on import
    //static to also make sure when we create test data we don't get overlapping timestamps
	static OBJ_CAT=0; static OBJ_ENTRY=1;  static OBJ_ATTRIBUTE=2;
	static OBJ_NAMES = ["cat", "entry", "attribute"];
	static async EnsureUniqueTimeStamp(obj_db:any, OBJ_TYPE:number)
	{
		let count_with_timestamp=0;

		if(OBJ_TYPE==ExportImport_.OBJ_CAT)
			count_with_timestamp = await Data.GetCategoriesCountByTimestamp(obj_db.timestamp)
		else if(OBJ_TYPE==ExportImport_.OBJ_ENTRY)
			count_with_timestamp = await Data.GetEntryCountByTimestamp(obj_db.timestamp)
		else if(OBJ_TYPE==ExportImport_.OBJ_ATTRIBUTE)
			count_with_timestamp = await Data.GetAttributeCountByTimestamp(obj_db.timestamp)

		console.log("EnsureUniqueTimeStamp: added new "+ExportImport_.OBJ_NAMES[OBJ_TYPE], obj_db, "count with curent timestamp: ", count_with_timestamp);

		//-------------------for making sure no duplicated timestamps----------------
		if(count_with_timestamp>1)
		{
			console.log(`EnsureUniqueTimeStamp: found duplicate ${ExportImport_.OBJ_NAMES[OBJ_TYPE]} timestamps`, obj_db);
			let time_stamp_duplicated=true;
			let count=0;

            let timestamp = obj_db.timestamp;
			//have a duplicate time stamp, so need to find one that isn't duplicated
			while(time_stamp_duplicated)
			{
				timestamp++;
                obj_db.timestamp=timestamp;

                console.log(`EnsureUniqueTimeStamp: found duplicate2 ${ExportImport_.OBJ_NAMES[OBJ_TYPE]} timestamps, trying timestamp: `, timestamp, obj_db);
				
				//update with new timestamp
				if(OBJ_TYPE==ExportImport_.OBJ_CAT)
				{
					await Data.UpdateCat(obj_db);
					if(await Data.GetCategoriesCountByTimestamp(timestamp)<2)
						time_stamp_duplicated=false;
				}
				else if(OBJ_TYPE==ExportImport_.OBJ_ENTRY)
				{
                    // console.error("ERROR: timestamp duplicate, going to update entry db as: ",obj_db );
					await Data.UpdateEntry(obj_db);
					if(await Data.GetEntryCountByTimestamp(timestamp)<2)
						time_stamp_duplicated=false;
				}
				else if(OBJ_TYPE==ExportImport_.OBJ_ATTRIBUTE)
				{
					await Data.UpdateAttribute(obj_db);
					if(await Data.GetAttributeCountByTimestamp(timestamp)<2)
						time_stamp_duplicated=false;
				}
				
				if(count>50)//error
				{
					let error = `Tried to find a unque timestamp for ${ExportImport_.OBJ_NAMES[OBJ_TYPE]}: \n`+ JSON.stringify(obj_db)+"\nbut couldn't find one in 500 tries. That means there are over 500 with same date- so change date/ time to fix. (or I have to increase beyond 500 tries)";
					console.error(error);
					// throw new Error(error);
					break; //doesn't hit here
				}
				count++
			}
		}
		//-------------------END OF for making sure no duplicated timestamps----------------
	}

/*
New Import- for user->dates->entries
*/
    async ImportJSON(json:string)
    {
        //convert to json obj
        let json_obj;
        try
        {
            // json_obj  = JSON.parse(json);
            json_obj  = JSON5.parse(json);
        }
        catch (error) 
        {
            alert(error);
            // Expected output: ReferenceError: nonExistentFunction is not defined
            // (Note: the exact output may be browser-dependent)
          }

        //so should be organized as opposite as before- users in an array
        console.log("going to import JSON obj back into database:  ",json_obj);
        
        //lets go through the users
        for (const user of json_obj.Users) 
        {
            console.log("Looking at user: ", user);

            console.log("debugging cats, user ",user);

            //first check if user is already in database, based on timestamp
			let user_db;
			if(user.timestamp)
            	user_db = await Data.GetUserByTimeStamp(user.timestamp);
            console.log("found user_db as: ",user_db)

            //if they're not already in, import them as a new user (new user ID), and import everything else
            if(!user_db) //for new user
            {
                //first add the user from the json
                user_db = await Data.AddUserNoCats(user.name);

                //add info from the json for the user
                user_db.date_updated = Helpers.DateFromHumanReadable(user.date_updated) || new Date();

                //should use their original timestamp
                // user_db.timestamp = user_db.date_updated.getTime();
                //need to update the timestamp in database if they have one first
                if(user.timestamp)
                {
                    Data.UpdateUserTimeStampFromID(user_db.id, user.timestamp)
                    user_db.timestamp = user.timestamp;
                }

                await Data.UpdateUser(user_db);

                
                console.log("User wasn't in database, user is now: ", user_db);

                //create a new cat 

                //then add cats if they have them- if the user wasn't in the database before, their cats/ etc won't be
                if(user.cats)//in case they didn't have any
                for (const cat_json of user.cats) 
                {
                    console.log("importing cat to json: ", cat_json)
                    // let cat = JSON.parse(cat_json);
                    let cat = cat_json;

                    cat.date_updated = Helpers.DateFromHumanReadable(cat.date_updated) || new Date();

                    console.log("going to import cat: ", cat.name, cat, "cat.date_updated", cat.date_updated)

                   let name, type, timestamp, date_updated, color_bg, color_txt, icon, entries;
                   name = cat.name;
                   type = cat.type;
                   timestamp = cat.timestamp||(cat.date_updated as Date).getTime();
                   date_updated = cat.date_updated;
                   color_bg = cat.color_bg;
                   color_txt = cat.color_txt;
                   icon = cat.icon;
                   entries = cat.entries;


                   //destructuring https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Destructuring_assignment
                    let cat_obj = {id_user: user_db.id, name, type, timestamp, date_updated, color_bg, color_txt, icon};//everythign but entries to pass into catdb

                    //should make sure cat doesn't exist beforehand to be safe/ if they duplicated a cat
                    //just add the cat- since new user 
                    let cat_db = await Data.AddCat(cat_obj);

                    console.log("finished importing cat, cat is: ", cat_db)

					await ExportImport_.EnsureUniqueTimeStamp(cat_db, ExportImport_.OBJ_CAT);

                }//end of cats


                if(user.dates)//in case they didn't have any
                for (const date_json of user.dates)
                {
                    console.log("debugging cats, date_json ",date_json);
                    let date_y_m_d = date_json.date;//yy/m/d
                    for (const entry_json of date_json.entries)
                    {
                        console.log("debugging cats being set for entry: ",entry_json);
                        //item types
                        //have to go through all the entries, look for item types, and build out a list of those

                        //get the cat id by name + user_id
                        let cat = await Data.GetCategoryByUserIdAndCatName(user_db.id, entry_json.cat);

                        if(!cat)//if they forgot to put the cats at the bottom of the json, or created a new one, have to create it
                            cat = await Data.AddCat(new CatDB({name:entry_json.cat, id_user: user_db.id}).SetDefaults());


                        let cat_id = cat?.id;
                        console.log("debugging cats being set for entry: ",entry_json, "got cat: ", cat);

                         // let entry = JSON.parse(entry_json);
                         let entry = {...entry_json};
                         entry.id=undefined;
                         entry.id_user=user_db.id;
                         entry.id_cat=cat_id;

                        //  entry.date_updated = entry.date_updated?Helpers.DateFromHumanReadable(date_y_m_d+ " " +entry.date_updated):new Date(); //add back in the date to the time
                        entry.date_updated = Helpers.DateFromHumanReadable(date_y_m_d+ " " +entry.date_updated)

                         //for date finished- if same date, we only save out time, otherwise save out date as well. So need to first check if we saved out date
                         if(entry.date_finished)
                         {
                            if(entry.date_finished.indexOf("/")!=-1)//if we saved out the date/ it's a different finish date
                                entry.date_finished = Helpers.DateFromHumanReadable(entry.date_finished);//could have a different end date, so didn't strip off the date on the time
                            else
                                entry.date_finished = Helpers.DateFromHumanReadable(date_y_m_d+ " " +entry.date_finished);

                            entry.has_duration=true;
                         }

 
                         //also have to set the entry attribute id's to undefined, to give them new ones- also need to stop saving them out
                         if(entry.attributes)//in case they didn't have any
                            for(let attr of entry.attributes)
                            {
                                attr.id=undefined;

                                attr.id_user=user_db.id;
                                attr.id_cat=cat_id;
                            }
                         //go through and grab all the item types

                         //check if item type already exists- if so, use its id, if not create it
                         let item_type_db = await Data.GetItemTypeByName(entry.name, user_db.id);
 
                         let id_item_type=-1;
 
                         if(item_type_db!=undefined)//if not already defined
                             id_item_type= item_type_db.id;
 
                         entry.id_item_type = id_item_type;
 
                         //we have the name as "item_type" when we create a new entry- have to change name (or add to it)
                         entry.item_type=entry.name;
                         entry.name=undefined;
                         
                         let entry_db = await Data.AddEntry(entry, true);
                         console.log("added entry_db: ", entry_db);
 
 
                         //make sure attributes have unique timestamps
                         //have to first get all attributes for this entry
                         let attrs = await Data.GetAttributesForEntryID(entry_db.id)
                         for (const attr of attrs) 
                         {
                             await ExportImport_.EnsureUniqueTimeStamp(attr, ExportImport_.OBJ_ATTRIBUTE);
                         }

                    }//end of entries
                }//end of dates

                //NEW- now have to go through dates, and then entries

                
            }//end of if new user

            else// if user exists in database/ found by timestamp
            {
                //so have to go through, update user, then look through the cats imported, add them,

                //so use the user(user_db), then look through the entries to add them to thedatabase

                //update anything about the user that might have changed
                let user_updated = {...user};
                user_updated.date_updated = Helpers.DateFromHumanReadable(user.date_updated) || new Date();
                await Data.UpdateUser(user_updated);
                console.log("updating user with info: ", user_updated, "user was: ", user)


                


                if(user.dates)//in case they didn't have any
                for (const date_json of user.dates)
                {
                    let date_y_m_d = date_json.date;//yy/m/d
                    console.log("Entry Debug: date: ",date_y_m_d, "date_json.entries.length: ", date_json.entries.length, "date_json.entries: ", date_json.entries);
                    if(date_json.entries)
                    for (const entry_json of date_json.entries)
                    {
                   //item types
                   //have to go through all the entries, look for item types, and build out a list of those
                   
                        // let entry = JSON.parse(entry_json);
                        let entry = {...entry_json};
                        

                        let cat = await Data.GetCategoryByUserIdAndCatName(user_db.id, entry_json.cat);

                        if(!cat)//if they forgot to put the cats at the bottom of the json, or created a new one, have to create it
                            cat = await Data.AddCat(new CatDB({name:entry_json.cat, id_user: user_db.id}).SetDefaults());

                        let cat_id = cat?.id;

                        entry.id=undefined; //just because I accidentally have id's being exported
                        entry.id_user=user_db.id;
                        entry.id_cat=cat_id;

                        console.log("Entry Debug: looking at entry: ", entry);

                        //check if dates are blank- if not,m convert them, if they are, use default values
                        //entry.date_updated = entry.date_updated?Helpers.DateFromHumanReadable(date_y_m_d+ " " +entry.date_updated):new Date(); //add back in the date to the time
                        entry.date_updated = Helpers.DateFromHumanReadable(date_y_m_d+ " " +entry.date_updated)

                        //for date finished- if same date, we only save out time, otherwise save out date as well. So need to first check if we saved out date
                        if(entry.date_finished)
                         {
                            if(entry.date_finished.indexOf("/")!=-1)//if we saved out the date/ it's a different finish date
                                entry.date_finished = Helpers.DateFromHumanReadable(entry.date_finished);//could have a different end date, so didn't strip off the date on the time
                            else
                                entry.date_finished = Helpers.DateFromHumanReadable(date_y_m_d+ " " +entry.date_finished);
                            
                            entry.has_duration=true;
                         }

                        // entry.date_finished = Helpers.DateFromHumanReadable(entry.date_finished) || undefined;//could have a different end date, so didn't strip off the date on the time

                        
                        //go through and grab all the item types

                        //check if item type already exists- if so, use its id, if not create it
                        let item_type_db = await Data.GetItemTypeByName(entry.name, user_db.id); 

                        let id_item_type=-1;

                        if(item_type_db!=undefined)//if not already defined
                            id_item_type= item_type_db.id;

                        entry.id_item_type = id_item_type;

                        //we have the name as "item_type" when we create a new entry- have to change name (or add to it)
                        entry.item_type=entry.name;
                        entry.name=undefined;

                        //make sure entry doesn't already exist in database, if it does, just update it if changes, otherwise just add it

                        //get entry by date updated
                        console.log("Looking for entry", entry.item_type, "with date: ", entry.date_updated, entry.id_cat);

                        // let entry_db = await Data.GetEntryByDateUpdated(entry.date_updated, entry.id_item_type);
                        let entry_db;
						if(entry.timestamp)
							entry_db = await Data.GetEntryByTimeStamp(entry.timestamp);


                        //also have to set the entry attribute id's to undefined, to give them new ones- also need to stop saving them out
                        if(entry.attributes)//in case they didn't have any
                        for(let attr of entry.attributes)
                        {
                            attr.id=undefined;

                            attr.id_user=user_db.id;
                            attr.id_cat=cat_id;

                            if(entry_db)
                                attr.id_entry=entry_db.id;

                            //also convert attribute date_updated to readable if exists
                            if(attr.date_updated)
                                attr.date_updated = Helpers.DateFromHumanReadable(attr.date_updated);
                        }

                        if(entry_db)
                        {
                            console.log("Entry Debug: going to update entry db, entrydb found with timestamp: ", entry_db, "and the entry: ",entry);
                            entry.id=entry_db.id;
                           let update_entry = {cat_index:entry_db.id_cat,  entry_id: entry_db.id, entry:entry}

                           //shouldn't update entry if it is the same, or will update cat updated time/ mess up sorting
                           if(!new EntryDB(entry_db).IsEqual(entry))                            
                           {
                                console.log("Entry modified, so going to update: entry: ", entry, "entry in db: ", entry_db);
                                await Data.UpdateEntry(update_entry);                             
                           }
                        }
                        else
                        {
                            console.log("didn't find entry, so creating a new one");
                            // console.log("Temp not adding new entries", entry);
                            // entry_db = await Data.AddEntry(entry, true);
                            entry_db = await Data.AddEntry(entry);// should update the category if adding new entry  from json even
                            console.log("\n\n\nadded entry_db: ", entry_db);

							await ExportImport_.EnsureUniqueTimeStamp(entry_db, ExportImport_.OBJ_ENTRY);


							//make sure attributes have unique timestamps
							//have to first get all attributes for this entry
							let attrs = await Data.GetAttributesForEntryID(entry_db.id)
							for (const attr of attrs) 
							{
								await ExportImport_.EnsureUniqueTimeStamp(attr, ExportImport_.OBJ_ATTRIBUTE);
							}
                        }
                        
                        

                    }//end of entries
                }//end of dates



                
                
                //putting cat importing at the end of entries- so if they update a cat name, it won't create a new one when entries are using old names (though will if they update everything properly)
                if(user.cats)//in case they didn't have any
                for (const cat_json of user.cats) 
                {
                    // console.log("importing cat to json: ", cat_json)
                    // let cat = JSON.parse(cat_json);
                    let cat = cat_json;

                    cat.date_updated = Helpers.DateFromHumanReadable(cat.date_updated) || new Date();

                   let name, type, timestamp, date_updated, color_bg, color_txt, icon, entries;
                   name = cat.name;
                   type = cat.type;

                   if(cat.timestamp)
                		timestamp = parseInt(cat.timestamp);
                    else
                        timestamp = (cat.date_updated as Date).getTime()
                   date_updated = cat.date_updated;
                   color_bg = cat.color_bg;
                   color_txt = cat.color_txt;
                   icon = cat.icon;
                   entries = cat.entries;

                    let cat_obj = {id_user: user_db.id, name, type, timestamp, date_updated, color_bg, color_txt, icon};//everythign but entries to pass into catdb

                    //have to first check if the cat is already in the database, and use that if it is
                    // let cat_db = await Data.GetCategoryByUserIdAndCatName(user_db.id, cat.name);
                    let cat_db;
                    
					if(timestamp) //if new cat, no timestamp
					 	cat_db = await Data.GetCategoryByTimestamp(timestamp);

                    //if cat already exists            
                    if(cat_db)
                    {
                        console.log("cat already exists, no need to create it");
                        //updating cat:
                        (cat_obj as any).id=cat_db.id;
                        await Data.UpdateCat(cat_obj);
                    }
                    else //if it doesn't exist, add it
                    {
                        cat_db = await Data.AddCat(cat_obj);
                        console.log("added new cat: ", cat_db);
						await ExportImport_.EnsureUniqueTimeStamp(cat_db, ExportImport_.OBJ_CAT);
                    }

                }//end of cats


                
            }//end of else / user exists already
        }
    }






    /*
    Old import statement- for user->cat->entries 
    */
    async ImportJSONByCat(json:string)
    {
        //convert to json obj
        let json_obj  = JSON.parse(json);

        //so should be organized as opposite as before- users in an array
        console.log("going to import JSON obj back into database:  ",json_obj);
        
        //lets go through the users
        for (const user of json_obj.Users) 
        {
            console.log("Looking at user: ", user);

            //first check if user is already in database, based on timestamp
			let user_db;
			if(user.timestamp)
            	user_db = await Data.GetUserByTimeStamp(user.timestamp);
            console.log("found user_db as: ",user_db)

            //if they're not already in, import them as a new user (new user ID), and import everything else
            if(!user_db)
            {
                //first add the user from the json
                user_db = await Data.AddUserNoCats(user.name);

                //add info from the json for the user
                user_db.date_updated = Helpers.DateFromHumanReadable(user.date_updated) || new Date();

                //should use their original timestamp
                // user_db.timestamp = user_db.date_updated.getTime();
                //need to update the timestamp in database if they have one first
                if(user.timestamp)
                {
                    Data.UpdateUserTimeStampFromID(user_db.id, user.timestamp)
                    user_db.timestamp = user.timestamp;
                }

                await Data.UpdateUser(user_db);

                
                console.log("User wasn't in database, user is now: ", user_db);

                //create a new cat 

                //then add cats if they have them- if the user wasn't in the database before, their cats/ etc won't be
                if(user.cats)//in case they didn't have any
                for (const cat_json of user.cats) 
                {
                    console.log("importing cat to json: ", cat_json)
                    // let cat = JSON.parse(cat_json);
                    let cat = cat_json;

                    cat.date_updated = Helpers.DateFromHumanReadable(cat.date_updated) || new Date();

                    //create the cat
                    /* 
                    Cat format:
                    let cat = {id:cat_db.id, name:cat_db.name, date_updated:Helpers.DateToHumanReadable(cat_db.date_updated), 
                    color_bg: cat_db.color_bg,
                    color_txt: cat_db.color_txt,
                    icon: cat_db.icon,
                    entries: new Array(),
                    };
                    */
                   //need also id_user

                   //error/ says it's not iterable when trying to do it the easy way
                //    let [name, date_updated, color_bg, color_txt, icon, entries] = {...cat};
                   let name, timestamp, date_updated, color_bg, color_txt, icon, entries;
                   name = cat.name;
                   timestamp = cat.timestamp;
                   date_updated = cat.date_updated;
                   color_bg = cat.color_bg;
                   color_txt = cat.color_txt;
                   icon = cat.icon;
                   entries = cat.entries;


                   //destructuring https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Destructuring_assignment
                    let cat_obj = {id_user: user_db.id, name, timestamp, date_updated, color_bg, color_txt, icon};//everythign but entries to pass into catdb



					



                    // let cat_db = new CategoryData(cat_obj);
                    let cat_db = await Data.AddCat(cat_obj);

					//for testing duplicate timestamps
					// console.warn("setting all time stamps to be the same to see if it can fix it")
					// cat_db.timestamp=0;
					// await Data.UpdateCat(cat_db);

					await ExportImport_.EnsureUniqueTimeStamp(cat_db, ExportImport_.OBJ_CAT);

					/* let count_with_timestamp = await Data.GetCategoriesCountByTimestamp(cat_db.timestamp)
                    console.log("added new cat: ", cat_db, "count with curent timestamp: ", count_with_timestamp);

					//-------------------for making sure no duplicated timestamps----------------
					if(count_with_timestamp>1)
					{
						console.log("found duplicate cats timestamps");
						let time_stamp_duplicated=true;
						let count=0;
						//have a duplicate time stamp, so need to find one that isn't duplicated
						while(time_stamp_duplicated)
						{
							cat_db.timestamp++;
							
							//update with new timestamp
							await Data.UpdateCat(cat_db);

							if(await Data.GetCategoriesCountByTimestamp(cat_db.timestamp)<2)
								time_stamp_duplicated=false;
							if(count>500)//error
							{
								let error = "Tried to find a unque timestamp for cat: \n"+ JSON.stringify(cat_obj)+"\nbut couldn't find one in 500 tries. That means there are over 500 with same date- so change date/ time to fix. (or I have to increase beyond 500 tries)";
								console.error(error);
								// throw new Error(error);
								break; //doesn't hit here
							}
							count++
						}
					} */
					//-------------------END OF for making sure no duplicated timestamps----------------


                   

                   //item types
                   //have to go through all the entries, look for item types, and build out a list of those
                   if(cat.entries)//in case they didn't have any
                   for (const entry_json of cat.entries)
                   {
                        // let entry = JSON.parse(entry_json);
                        let entry = {...entry_json};
                        entry.id=undefined;
                        entry.id_user=user_db.id;
                        entry.id_cat=cat_db.id;
                        entry.date_updated = Helpers.DateFromHumanReadable(entry.date_updated) || new Date();
                        entry.date_finished = Helpers.DateFromHumanReadable(entry.date_finished) || undefined;
                        // entry.timestamp=cat_db.timestamp;

                        //also have to set the entry attribute id's to undefined, to give them new ones- also need to stop saving them out
                        if(entry.attributes)//in case they didn't have any
                        for(let attr of entry.attributes)
                        {
                            attr.id=undefined;

                            attr.id_user=user_db.id;
                            attr.id_cat=cat_db.id;
                        }
                        //go through and grab all the item types

                        //check if item type already exists- if so, use its id, if not create it
                        let item_type_db = await Data.GetItemTypeByName(entry.name, user_db.id);

                        let id_item_type=-1;

                        if(item_type_db!=undefined)//if not already defined
                            id_item_type= item_type_db.id;

                        entry.id_item_type = id_item_type;

                        //we have the name as "item_type" when we create a new entry- have to change name (or add to it)
                        entry.item_type=entry.name;
                        entry.name=undefined;
                        
                        let entry_db = await Data.AddEntry(entry);
                        console.log("added entry_db: ", entry_db);


						//make sure attributes have unique timestamps
						//have to first get all attributes for this entry
						let attrs = await Data.GetAttributesForEntryID(entry_db.id)
						for (const attr of attrs) 
						{
							await ExportImport_.EnsureUniqueTimeStamp(attr, ExportImport_.OBJ_ATTRIBUTE);
						}
						

                    }

                    //entries 

                    

                    //entry attributes
                }

                
                

                
            }

            else// if user exists in database/ found by timestamp
            {
                //so have to go through, update user, then look through the cats imported, add them,

                //so use the user(user_db), then look through the entries to add them to thedatabase

                //update anything about the user that might have changed
                let user_updated = {...user};
                user_updated.date_updated = Helpers.DateFromHumanReadable(user.date_updated) || new Date();
                await Data.UpdateUser(user_updated);
                console.log("updating user with info: ", user_updated, "user was: ", user)


                if(user.cats)//in case they didn't have any
                for (const cat_json of user.cats) 
                {
                    // console.log("importing cat to json: ", cat_json)
                    // let cat = JSON.parse(cat_json);
                    let cat = cat_json;

                    cat.date_updated = Helpers.DateFromHumanReadable(cat.date_updated) || new Date();

                   let name, timestamp, date_updated, color_bg, color_txt, icon, entries;
                   name = cat.name;

                   if(cat.timestamp)
                		timestamp = parseInt(cat.timestamp);
                   date_updated = cat.date_updated;
                   color_bg = cat.color_bg;
                   color_txt = cat.color_txt;
                   icon = cat.icon;
                   entries = cat.entries;



                    let cat_obj = {id_user: user_db.id, name, timestamp, date_updated, color_bg, color_txt, icon};//everythign but entries to pass into catdb


                    //have to first check if the cat is already in the database, and use that if it is
                    // let cat_db = await Data.GetCategoryByUserIdAndCatName(user_db.id, cat.name);
                    let cat_db;
					if(timestamp) //if new cat, no timestamp
					 	cat_db = await Data.GetCategoryByTimestamp(timestamp);

                    //if cat already exists            
                    if(cat_db)
                    {
                        console.log("cat already exists, no need to create it");
                        //updating cat:
                        (cat_obj as any).id=cat_db.id;
                        await Data.UpdateCat(cat_obj);
                    }
                    else //if it doesn't exist, add it
                    {
                        cat_db = await Data.AddCat(cat_obj);
                        console.log("added new cat: ", cat_db);

						//for testing duplicate timestamps
						// console.warn("setting all time stamps to be the same to see if it can fix it")
						// cat_db.timestamp=0;
						// await Data.UpdateCat(cat_db);

						await ExportImport_.EnsureUniqueTimeStamp(cat_db, ExportImport_.OBJ_CAT);
                    }

                   //item types
                   //have to go through all the entries, look for item types, and build out a list of those
                   if(cat.entries)//in case they didn't have any
                   for (const entry_json of cat.entries)
                   {
                        // let entry = JSON.parse(entry_json);
                        let entry = {...entry_json};
                        entry.id=undefined; //just because I accidentally have id's being exported
                        entry.id_user=user_db.id;
                        entry.id_cat=cat_db.id;

                        //check if dates are blank- if not,m convert them, if they are, use default values
                        entry.date_updated = entry.date_updated?Helpers.DateFromHumanReadable(entry.date_updated) : new Date();
                        entry.date_finished = entry.date_finished?Helpers.DateFromHumanReadable(entry.date_finished): undefined;

                        
                        //go through and grab all the item types

                        //check if item type already exists- if so, use its id, if not create it
                        let item_type_db = await Data.GetItemTypeByName(entry.name, user_db.id); 

                        let id_item_type=-1;

                        if(item_type_db!=undefined)//if not already defined
                            id_item_type= item_type_db.id;

                        entry.id_item_type = id_item_type;

                        //we have the name as "item_type" when we create a new entry- have to change name (or add to it)
                        entry.item_type=entry.name;
                        entry.name=undefined;

                        //make sure entry doesn't already exist in database, if it does, just update it if changes, otherwise just add it

                        //get entry by date updated
                        console.log("Looking for entry", entry.item_type, "with date: ", entry.date_updated, entry.id_cat);

                        // let entry_db = await Data.GetEntryByDateUpdated(entry.date_updated, entry.id_item_type);
                        let entry_db;
						if(entry.timestamp)
							entry_db = await Data.GetEntryByTimeStamp(entry.timestamp);


                        //also have to set the entry attribute id's to undefined, to give them new ones- also need to stop saving them out
                        if(entry.attributes)//in case they didn't have any
                        for(let attr of entry.attributes)
                        {
                            attr.id=undefined;

                            attr.id_user=user_db.id;
                            attr.id_cat=cat_db.id;

                            if(entry_db)
                                attr.id_entry=entry_db.id;

                            //also convert attribute date_updated to readable if exists
                            if(attr.date_updated)
                                attr.date_updated = Helpers.DateFromHumanReadable(attr.date_updated);
                        }

                        if(entry_db)
                        {
                            //update it if anything has changed

                            //need to provide it:
                            /*
                            interface UpdateEntryInterface {
                            cat_index: number,
                            entry_index: number, //not used for updating- so don't need to actually set
                            entry_id: number,
                            entry:Entry
                        }
                            */
                            entry.id=entry_db.id;
                           let update_entry = {cat_index:entry_db.id_cat,  entry_id: entry_db.id, entry:entry}
                            
                            await Data.UpdateEntry(update_entry);
                            console.log("Found entry, and updated: ", entry);
                        }
                        else
                        {
                            console.log("didn't find entry, so creating a new one");
                            // console.log("Temp not adding new entries", entry);
                            entry_db = await Data.AddEntry(entry);
                            console.log("added entry_db: ", entry_db);

							await ExportImport_.EnsureUniqueTimeStamp(entry_db, ExportImport_.OBJ_ENTRY);


							//make sure attributes have unique timestamps
							//have to first get all attributes for this entry
							let attrs = await Data.GetAttributesForEntryID(entry_db.id)
							for (const attr of attrs) 
							{
								await ExportImport_.EnsureUniqueTimeStamp(attr, ExportImport_.OBJ_ATTRIBUTE);
							}
                        }
                        
                        

                    }

                    //entries 

                    

                    //entry attributes
                }
            }
        }
    }
}

export const ExportImport = new ExportImport_();